import {UniqueQueue} from "./uniqueQueue";
import truncate from "truncate-utf8-bytes";
import tokenService from "./jwtTokenHandler";
import {AudienceType} from "../definitions/enums";
import {CrossSellType, Nullable} from "../definitions/types";
import {CrossSellModel} from "../models/domain/crossSellModel";
import {ServerError} from "@apollo/client";

const localStorageKey = "localStorage";
const recentlyViewedKey = "recentlyViewed";
const illegalRegex = /[\/?<>\\:*|"]/g;
const controlRegex = /[\x00-\x1f\x80-\x9f]/g;
const reservedRegex = /^\.+$/;
const windowsReservedRegex = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
const unauthorized = 'UnauthorizedException';
const unauthorizedMessage = 'You are not authorized to make this call.';

type ErrorType = {
    "errorType": string,
    "message": string
};

export const getTimeAgo = (date: Date) => {
    const now = new Date();

    const seconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);

    let interval = Math.floor(seconds / 31536000);

    // if the number of seconds is greater than or equal to 1 year, return the number of years
    if (interval > 0) return `${interval} year${interval === 1 ? '' : 's'} ago`;

    interval = Math.floor(seconds / 2592000);

    // if the number of seconds is greater than or equal to 1 month, return the number of months
    if (interval > 0) return `${interval} month${interval === 1 ? '' : 's'} ago`;

    interval = Math.floor(seconds / 86400);

    // if the number of seconds is greater than or equal to 1 day, return the number of days
    if (interval > 0) return `${interval} day${interval === 1 ? '' : 's'} ago`;

    interval = Math.floor(seconds / 3600);

    // if the number of seconds is greater than or equal to 1 hour, return the number of hours
    if (interval > 0) return `${interval} hour${interval === 1 ? '' : 's'} ago`;

    interval = Math.floor(seconds / 60);

    // if the number of seconds is greater than or equal to 1 minute, return the number of minutes
    if (interval > 0) return `${interval} minute${interval === 1 ? '' : 's'} ago`;

    // if the number of seconds is less than 1 minute, return the number of seconds
    return `${Math.floor(seconds)} seconds ago`;
}

/* https://stackoverflow.com/questions/46155/whats-the-best-way-to-validate-an-email-address-in-javascript */
const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validateEmail = (email: string) => regexMatch(email, emailRegEx);

const regexMatch = (email: string, regexPattern: RegExp) => String(email).toLowerCase().match(regexPattern)

export const currencyFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});

export const recentlyViewed = () => {
    try {
        // Filter logic:
        // 1. Remove null
        // 2. Remove NaN
        // 3. Remove negative numbers
        // 4. Remove duplicates
        return new UniqueQueue<Nullable<string | number>>(12, JSON.parse(localStorage[recentlyViewedKey]))
            .values()
            .reverse()
            .filter((id): id is string | number => id !== null)
            .filter(id => !isNaN(Number(id)))
            .map(id => Number(id))
            .filter(id => id > 0)
            .filter((value, index, self) => self.indexOf(value) === index);
    } catch (e) {
        return [] as number[];
    }
};

export const addRecentlyViewed = (productId: number) => {
    if (productId === null || isNaN(productId) || !supportsLocalStorage()) return;
    let recentlyViewed: UniqueQueue<string>;
    try {
        recentlyViewed = new UniqueQueue(12, JSON.parse(localStorage[recentlyViewedKey]));
    } catch (e) {
        recentlyViewed = new UniqueQueue<string>(12);
    }
    recentlyViewed.enqueue(String(productId));
    localStorage[recentlyViewedKey] = JSON.stringify(recentlyViewed.values());
};

function supportsLocalStorage() {
    try {
        localStorage.setItem(localStorageKey, localStorageKey);
        localStorage.removeItem(localStorageKey);
        return true;
    } catch (e) {
        return false;
    }
}

export const crossSellResponsive = {
    largeDesktop: {
        breakpoint: {max: 4000, min: 1200},
        items: 5,
        partialVisibilityGutter: 10
    },
    desktop: {
        breakpoint: {max: 1200, min: 768},
        items: 4,
        partialVisibilityGutter: 10
    },
    largeMobile: {
        breakpoint: {max: 768, min: 576},
        items: 3,
        partialVisibilityGutter: 10
    },
    mobile: {
        breakpoint: {max: 576, min: 440},
        items: 2,
        partialVisibilityGutter: 10
    },
    extraSmallMobile: {
        breakpoint: {max: 440, min: 0},
        items: 2,
        partialVisibilityGutter: 0
    }
};

export const initialResourceItemCount = 5;

export const acceptedFileExtensions = ['.jpg', '.jpeg', '.psd', '.ai', '.eps', '.tif', '.tiff', '.pdf', '.zip', '.rar'];

export const sanitize: (input: string, replacement?: string) => string = (input: string, replacement: string = "_") => truncate(input
    .replace(illegalRegex, replacement)
    .replace(controlRegex, replacement)
    .replace(reservedRegex, replacement)
    .replace(windowsReservedRegex, replacement)
    .replace(/ /g, replacement)
    .replace(/&/g, replacement), 255);

export const identity = () => {
    const claims = tokenService.decode();

    return {
        claims: claims,
        isAuthenticated: claims.aud === AudienceType.Accounts,
        isAnonymous: claims.aud === AudienceType.Guests,
        isUserContentAdmin: claims.uca === true,
        isInternalUser: claims.iiu === true,
        accountId: claims.aud === AudienceType.Accounts ? Number(claims.sub) : 0,
        email: claims.aud === AudienceType.Accounts ? claims.email : undefined,
        exp: claims.exp
    }
};

const ConvertToCrossSellModel = (source: CrossSellType) => {
    const image = {
        url: source.imageSrc,
        alt: source.imageAlt,
        title: source.imageTitle
    };

    return new CrossSellModel({
        ...source,
        id: source.productId,
        variantPath: source.path,
        minQuantity: source.minQuantity ?? 1
    }, image, {...source}, source.ratingStatistics)
}

export const mapper = {
    crossSellToModel: ConvertToCrossSellModel
}

export interface ICountdownDate {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

/**
 * @method computeCountdown
 * @description
 *  Computes the countdown in days, hours, minutes and seconds from one date to another.
 *  Does not allow negative dates due to round errors (would need to use a math.ceil when < 1)
 * @returns {days: number, hours: number, minutes: number, seconds: number}
 */
export function computeCountdown(
    currentDate: Date,
    futureDate: Date
): ICountdownDate {
    if (futureDate < currentDate)
        return {days: 0, hours: 0, minutes: 0, seconds: 0};
    const distance = futureDate.getTime() - currentDate.getTime();
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    return {days, hours, minutes, seconds};
}

export function formatCountdown({days, hours, minutes, seconds,}: ICountdownDate) {
    if (hours >= 24 || minutes >= 60 || seconds >= 60) return "";

    let message: string = "";

    if (days > 0)
        message += days + " day" + (days == 1 ? '' : 's') + " ";
    if (hours > 0 && hours < 24)
        message += hours + " hr" + (hours == 1 ? "" : "s") + " ";
    if (minutes > 0 && minutes < 60)
        message += minutes + " min" + (minutes == 1 ? "" : "s");
    if (days == 0 && hours == 0 && minutes == 0 && seconds > 0 && seconds < 60)
        message += seconds + " sec" + (seconds == 1 ? "" : "s");

    return message.trim();
}

export const catalogName = window.viewModel.catalogId === 1 ? 'Displays2go' : 'PostUp Stand'

export const returnUrl = `${window.location.pathname}${window.location.search}`;

/**
 * @method getHostedSiteId
 * @description
 * Returns the hosted site id for the given catalog id
 * @param {number} catalogId
 * @returns {number}
 * @throws {Error} Invalid catalog id
 */
export const getHostedSiteId = function (catalogId: number): number {
    if (catalogId === 1) return 1;
    if (catalogId === 2) return 109;
    throw new Error('Invalid catalogId');
}

export const truncateString = (string: string, maxLength: number) => {
    if (string.length <= maxLength) return string;
    const truncatedTitle = string.substring(0, maxLength);
    const lastSpaceIndex = truncatedTitle.lastIndexOf(' ');
    return lastSpaceIndex >= 0 ? truncatedTitle.substring(0, lastSpaceIndex) + '...' : truncatedTitle + '...';
};

export const isApolloUnauthorizedError = (originalException?: unknown) => {
    try {
        // If the original exception is not defined, return false
        if (!originalException) return false;

        // If the original exception is not an object, return false
        if (typeof originalException !== 'object') return false;

        // If the original exception does not have a property called 'name', return false
        if (!('name' in originalException)) return false;

        // If the value of the name property is not 'ServerError', return false
        if (originalException.name !== 'ServerError') return false;

        // If the original exception does not have a property called 'statusCode', return false
        if (!('statusCode' in originalException)) return false;

        // Deconstruct the original exception into statusCode and result
        const {statusCode, result} = originalException as ServerError;

        // If the statusCode is not 401, return false
        if (statusCode !== 401) return false;

        // If the result is not defined, return false
        if (!result) return false;

        // If the result is a string, return false
        if (typeof result === 'string') return false;

        // If the result does not have a property called 'errors', return false
        if (!('errors' in result)) return false;

        // Deconstruct the result into errors
        const {errors} = result;

        // Ensure that the error array contains an UnauthorizedException with the message 'Not Authorized
        // to access this resource'
        return errors.some((e: ErrorType) => e.errorType === unauthorized && e.message === unauthorizedMessage);
    } catch {
        // Do nothing
    }

    return false;
}

export function getDesignerPageUrl(primaryId: number | undefined, productId: number, slug: string | undefined, isDynamic: boolean, width: number | undefined, height: number | undefined) {
    let designerPageUrl = `/ProductContainer/FyreFly?itemId=${primaryId}&variantId=${productId}&slug=${slug}`;
    if (isDynamic) designerPageUrl += `&dynamicWidth=${width}&dynamicHeight=${height}`;
    return designerPageUrl;
}